<template>
  <Preloader v-if="localLoading" />
  <VRow v-else-if="errorMessage">
    <VCol cols="12">
      <VAlert type="error">
        {{ errorMessage }}
      </VAlert>
    </VCol>
  </VRow>
  <VRow v-else>
    <VCol cols="12">
      <VacancyStepTitle
        v-if="vacancy"
        :progress="90"
        :vacancy="vacancy"
        :step="5"
      >
        {{ $t('step') }}
        <template #description>
          <span v-html="$t('integrationsTip')" />
        </template>
      </VacancyStepTitle>

      <div class="mobile-container">
        <IntegrationsInviteMethod
          background-loading
          v-if="vacancy"
          ref="inviteMethod"
          :vacancy="vacancy"
        >
          <template #actions>
            <div />
          </template>
        </IntegrationsInviteMethod>
      </div>

      <VBtn
        dark
        color="success"
        outlined
        class="mt-3 mt-md-12 mr-3"
        elevation="0"
        @click="back"
      >
        {{ $t('goBack') }}
      </VBtn>

      <VBtn
        dark
        color="success"
        class="mt-3 mt-md-12"
        :loading="localLoading"
        @click="next"
        elevation="0"
      >
        {{ $t('next') }}
      </VBtn>
    </VCol>
  </VRow>
</template>

<script>
import VacancyStepTitle from '@/views/Vacancy/VacancyStepTitle';
import Wizard from '@/orm/models/Wizard/Wizard';
import { settingsEntityType, wizardSteps } from '@/enums';
import { computed, toRefs, ref } from '@vue/composition-api';
import useVacancies from '@/compositions/useVacancies';
import useValidation from '@/compositions/useValidation';
import IntegrationsInviteMethod from '@/components/Integrations/IntegrationsInviteMethod';
import Preloader from '../../components/Preloader.vue';

export default {
  name: 'VacancyStepIntegration',
  components: {
    IntegrationsInviteMethod,
    VacancyStepTitle,
    Preloader,
  },
  metaInfo() {
    return {
      title: this.$t('step'),
    };
  },
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
  },
  setup(props, { root }) {
    const inviteMethod = ref(null);
    const { vacancyId } = toRefs(props);
    const {
      publishVacancy, findVacancyById,
    } = useVacancies();
    const {
      withApiErrorHandling, withLoading, errorMessage, localLoading,
    } = useValidation();

    const vacancy = computed(() => {
      return findVacancyById(vacancyId.value);
    });

    function setState() {
      Wizard
        .setVacancyId(vacancyId.value)
        .setWizardStep(wizardSteps.INTEGRATION);
    }

    return {
      vacancy,
      publish: () => {
        publishVacancy(vacancyId.value, () => {
          root.$router.push({ name: 'candidates', params: { id: vacancyId.value.toString() } });
        }, (e) => {
          root.$toast.error(e.message);
        });
      },
      localLoading,
      back() {
        Wizard.pushPrevRoute(root.$router);
      },
      next() {
        inviteMethod.value.save();
        withLoading(async () => {
          await Wizard.saveWizardState();
          await Wizard.pushNextRoute(root.$router);
        });
      },
      prepare() {
        setState();
        withApiErrorHandling(async () => {
          await Promise.all([Wizard.fetchData()]);
        });
      },
      errorMessage,
      settingsEntityType,
      inviteMethod,
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => vm.prepare());
  },
  beforeRouteUpdate(to, from, next) {
    next((vm) => vm.prepare());
  },
};
</script>

<style lang="scss" scoped>
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    .mobile-container {
      width: calc(100% + 24px);
      margin-left: -12px;
    }
  }
</style>

<i18n locale="en" lang="yaml">
  step: "Integration"
  publish: Publish
  saveDraft: Save draft
  lostInfoWarning: Changes not saved will be lost. Are you sure?
  continue: Continue editing
  goBack: Go back
</i18n>

<i18n locale="ru" lang="yaml">
  step: "Настройка приглашения кандидатов"
  next: Далее
  saveDraft: Сохранить черновик
  integrationsTip: 'Huntica автоматически импортирует отклики кандидатов с Работа.ру.<br>
  Чтобы кандидаты смогли записать видеоотклик, им необходимо отправить приглашение.'
  lostInfoWarning: Все изменения будут потеряны. Вы уверены?
  continue: Продолжить редактирование
  goBack: Назад
</i18n>
